import React, { useEffect, useMemo, useState } from 'react';

import { BaseGym, Product } from '../../types/Gym';
import { User } from '../../types/authentication';

import { PassPicker } from './PassPicker';
import { PassPickerProductBlock } from './PassPickerProductBlock';
import { PassPickerOnlyMonthlyProductBlock } from './PassPickerOnlyMonthlyProductBlock';

import { LinkButton } from '@husslehq/ui-library';

import fakeDoorList from '../../pages/fake-door-list.json';

import styles from '../../components/PassPicker/Product.module.scss';

type Props = {
  products: Product[];
  gym: BaseGym;
  featured: string | string[] | undefined;
  user: User | undefined;
};

export const PassPickerBlock: React.FC<Props> = ({ products, gym, featured, user }) => {
  const [isProductBlockOpen, setIsProductBlockOpen] = useState(false);
  const [productsToShow, setProductsToShow] = useState('all');
  const dailyProduct = useMemo(() => products && products.find((product) => product.type === 'daypass'), [products]);
  const monthlyProduct = useMemo(() => products && products.find((product) => product.type === 'monthly'), [products]);
  const directProduct = useMemo(() => products && products.find((product) => product.type === 'direct'), [products]);
  const fakeDoorEntry = fakeDoorList.find((gymEntry) => gymEntry.GymId === parseInt(gym.id, 10));

  const hasMonthlyOffer =
    monthlyProduct?.discount ||
    (monthlyProduct?.original_price && monthlyProduct?.original_price > monthlyProduct?.price)
      ? true
      : false;

  const hasDailyOffer = dailyProduct?.discount ? true : false;

  const isRecommended = featured === ('direct' || 'multigym') ? true : false;

  const monthlyProductOnly =
    monthlyProduct &&
    (!dailyProduct || dailyProduct.purchasable == false) &&
    (!directProduct || directProduct.purchasable == false);
  const hasMoreThanTwoProducts = (!!fakeDoorEntry || directProduct) && dailyProduct && monthlyProduct;

  useEffect(() => {
    if (hasMoreThanTwoProducts && (isRecommended || hasMonthlyOffer || hasDailyOffer)) {
      setIsProductBlockOpen(true);
      setProductsToShow(hasMonthlyOffer || isRecommended ? 'monthly' : 'daily');
    }
  }, [gym]);

  const showPassPicker = hasMoreThanTwoProducts && !isProductBlockOpen;

  return (
    <div className={styles.product__container}>
      {gym.is_listed ? (
        showPassPicker ? (
          <PassPicker
            products={products}
            gym={gym}
            onSelectProduct={setProductsToShow}
            onShowProducts={setIsProductBlockOpen}
          />
        ) : monthlyProductOnly ? (
          <PassPickerOnlyMonthlyProductBlock
            monthlyProduct={monthlyProduct}
            gym={gym}
            isTemporarilyClosed={gym.is_temporarily_closed}
            user={user}
          />
        ) : (
          <PassPickerProductBlock
            featured={featured}
            products={gym.products}
            gym={gym}
            isTemporarilyClosed={gym.is_temporarily_closed}
            user={user}
            onClickEdit={() => setIsProductBlockOpen(false)}
            productsToShow={productsToShow}
          />
        )
      ) : (
        <div className={styles.product__notlisted}>
          <div className={styles['product__notlisted-title']}>
            This gym is currently not part of the Hussle program.
          </div>
          <LinkButton href={`/search?location=${gym.location.postcode}`} level="outline" size="large">
            Find a gym nearby
          </LinkButton>
        </div>
      )}
    </div>
  );
};
